/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * "Create with AI" for reports and dashboards.
 *
 * kind="report": the backend drafts and validates a report definition, which
 * opens unsaved in the report builder (location.state.draft). Nothing is
 * stored until the user saves it there.
 *
 * kind="dashboard": the backend drafts the dashboard and creates it, with its
 * reports and charts, in the user's private "AI generated" folder, then this
 * opens it.
 *
 * A draft the backend cannot validate comes back as a 422 whose message says
 * what was wrong; it is shown as it is so the user can rephrase.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useAuth } from "auth/Auth";
import useAiAssistantStatus from "hooks/useAiAssistantStatus";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";

const COPY = {
  report: {
    title: "Create a report with AI",
    hint: "Describe the report you want. You can review and change it in the builder before saving.",
    placeholder: "e.g. Open deals closing this quarter by stage, with total value",
    action: "Draft report",
    busy: "Drafting the report…",
  },
  dashboard: {
    title: "Create a dashboard with AI",
    hint: "Describe what the dashboard should show. It is created in your private \"AI generated\" folder, and you can edit it afterwards.",
    placeholder: "e.g. Sales overview: pipeline by stage, deals won this quarter, new leads by source",
    action: "Create dashboard",
    busy: "Building the dashboard…",
  },
};

export default function CreateWithAiDialog({ open, onClose, kind, initialPrompt }) {
  const { axiosInstance } = useAuth();
  const { refresh } = useAiAssistantStatus();
  const navigate = useNavigate();
  const copy = COPY[kind] || COPY.report;

  const [prompt, setPrompt] = useState(initialPrompt || "");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setPrompt(initialPrompt || "");
      setError(null);
    }
  }, [open, initialPrompt]);

  const submit = async () => {
    const text = prompt.trim();
    if (!text || working) return;
    setWorking(true);
    setError(null);
    try {
      if (kind === "dashboard") {
        const { data } = await axiosInstance.post("/analytics/ai/dashboard-draft", { prompt: text });
        onClose();
        navigate(`/dashboards/${data.id}`);
      } else {
        const { data } = await axiosInstance.post("/analytics/ai/report-draft", { prompt: text });
        onClose();
        navigate("/reports/new", { state: { draft: data } });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "That could not be created. Try describing it differently."));
      const status = err?.response?.status;
      if (status === 402 || status === 403) refresh();
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onClose={working ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesome sx={{ color: colors.brand.primary }} fontSize="small" />
        {copy.title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {copy.hint}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          value={prompt}
          disabled={working}
          placeholder={copy.placeholder}
          inputProps={{ maxLength: 2000 }}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {working && (
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {copy.busy}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={working}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={working || !prompt.trim()}>
          {copy.action}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

CreateWithAiDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  kind: PropTypes.oneOf(["report", "dashboard"]).isRequired,
  initialPrompt: PropTypes.string,
};
